import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Fab,
  Switch,
  FormControlLabel,
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  CheckCircle as CompleteIcon,
} from '@mui/icons-material';

interface Props {
  scadenzaId: string;
  scadenzaTitolo?: string;
  maxHeight?: string;
  showFilters?: boolean;
  realTimeUpdates?: boolean;
  allowAddNotes?: boolean;
  showExportButton?: boolean;
}

// Mock data per testing UX
const mockEvents = [
  {
    id: '1',
    tipo: 'CREAZIONE',
    timestamp: new Date('2025-11-19T09:00:00'),
    utente: 'Admin Sistema',
    descrizione: 'Scadenza creata nel sistema',
    dettagli: 'Creazione iniziale della scadenza per Autorizzazioni Trasporti'
  },
  {
    id: '2',
    tipo: 'MODIFICA',
    timestamp: new Date('2025-11-19T10:30:00'),
    utente: 'Laura Bianchi',
    descrizione: 'Aggiornamento giorni preavviso',
    dettagli: 'Giorni preavviso modificati da 15 a 30'
  },
  {
    id: '3',
    tipo: 'NOTA_AGGIUNTA',
    timestamp: new Date('2025-11-19T11:15:00'),
    utente: 'Admin Sistema',
    descrizione: 'Nota aggiunta',
    dettagli: 'Cliente contattato telefonicamente per verifica documenti'
  },
  {
    id: '4',
    tipo: 'INVIO_EMAIL',
    timestamp: new Date('2025-11-19T12:00:00'),
    utente: 'Sistema',
    descrizione: 'Email di reminder inviata',
    dettagli: 'Email inviata con successo a cliente@autoscuolaromacentro.it'
  }
];

const getEventIcon = (tipo: string) => {
  switch (tipo) {
    case 'CREAZIONE': return <AddIcon />;
    case 'MODIFICA': return <EditIcon />;
    case 'COMPLETAMENTO': return <CompleteIcon />;
    case 'NOTA_AGGIUNTA': return <HistoryIcon />;
    default: return <HistoryIcon />;
  }
};

const getEventColor = (tipo: string) => {
  switch (tipo) {
    case 'CREAZIONE': return 'success';
    case 'MODIFICA': return 'warning';
    case 'COMPLETAMENTO': return 'primary';
    case 'NOTA_AGGIUNTA': return 'info';
    default: return 'default';
  }
};

export default function TimelineComponentMock({
  scadenzaId,
  maxHeight = "550px",
  showFilters = true,
  allowAddNotes = true,
  showExportButton = true
}: Props) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showOnlyNotes, setShowOnlyNotes] = React.useState(false);
  const [expandedEvents, setExpandedEvents] = React.useState<Set<string>>(new Set());

  const filteredEvents = mockEvents.filter(event => 
    event.descrizione.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (!showOnlyNotes || event.tipo === 'NOTA_AGGIUNTA')
  );

  const toggleEventExpansion = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  return (
    <Box sx={{ height: maxHeight, display: 'flex', flexDirection: 'column' }}>
      {/* Filters Section */}
      {showFilters && (
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Cerca eventi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyNotes}
                  onChange={(e) => setShowOnlyNotes(e.target.checked)}
                  size="small"
                />
              }
              label="Solo Note"
              sx={{ ml: 1 }}
            />
          </Box>
          
          {showExportButton && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<ExportIcon />}
                variant="outlined"
                size="small"
              >
                Esporta PDF
              </Button>
              <Button
                startIcon={<FilterIcon />}
                variant="outlined" 
                size="small"
              >
                Filtri Avanzati
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Timeline Events */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: 2,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '3px',
        }
      }}>
        {filteredEvents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <HistoryIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
              Nessun evento trovato
            </Typography>
            <Typography variant="body2">
              {searchTerm ? 'Prova a modificare i filtri di ricerca' : 'Non ci sono eventi per questa scadenza'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {filteredEvents.map((event, index) => (
              <Box key={event.id} sx={{ position: 'relative' }}>
                {/* Vertical connector line - rendered before the card */}
                {index < filteredEvents.length - 1 && (
                  <Box 
                    sx={{
                      position: 'absolute',
                      left: 28, // Aligned with icon center (20px from left + 20px half-width)
                      top: 56, // Start from bottom of icon (16px padding + 40px icon height)
                      width: 2,
                      bottom: -16, // Connect to next card with gap
                      backgroundColor: 'action.disabled',
                      zIndex: 0,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: -2,
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: 'action.disabled',
                      }
                    }}
                  />
                )}
                
                <Card 
                  variant="outlined"
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative',
                    zIndex: 1,
                    backgroundColor: 'background.paper',
                    mb: 2,
                    '&:hover': {
                      boxShadow: 2,
                      transform: 'translateY(-1px)'
                    }
                  }}
                  onClick={() => toggleEventExpansion(event.id)}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* Event Icon with connector dots */}
                      <Box sx={{ position: 'relative', flexShrink: 0 }}>
                        <Box 
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: `${getEventColor(event.tipo)}.main`,
                            color: 'white',
                            position: 'relative',
                            zIndex: 2,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            border: '3px solid',
                            borderColor: 'background.paper'
                          }}
                        >
                          {getEventIcon(event.tipo)}
                        </Box>
                      </Box>

                      {/* Event Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={event.tipo.replace('_', ' ')}
                            size="small"
                            color={getEventColor(event.tipo) as any}
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {event.timestamp.toLocaleString('it-IT')}
                          </Typography>
                        </Box>
                        
                        <Typography variant="subtitle2" gutterBottom fontWeight={500}>
                          {event.descrizione}
                        </Typography>

                        {expandedEvents.has(event.id) && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {event.dettagli}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {event.utente}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Add Note FAB */}
      {allowAddNotes && (
        <Fab
          color="primary"
          size="small"
          aria-label="Aggiungi nota"
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
}